import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { slivWord } from 'pages/Learning/learningMeta';

interface LearningGoalRingProps {
  target: number;
  done: number;
  streak: number;
  size?: number;
  stroke?: number;
}

const FLAME = '#FF7043';

const LearningGoalRing: React.FC<LearningGoalRingProps> = ({
  target,
  done,
  streak,
  size = 116,
  stroke = 11,
}) => {
  const theme = useTheme();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = target > 0 ? Math.min(done / target, 1) : 0;
  const remaining = Math.max(target - done, 0);
  const trackColor = theme.palette.primary.purple ?? 'rgba(155,124,255,0.3)';

  return (
    <Stack direction={'row'} alignItems={'center'} spacing={2}>
      <Box
        sx={{
          flex: 'none',
          height: size,
          position: 'relative',
          width: size,
        }}
      >
        <Box
          component={'svg'}
          width={size}
          height={size}
          sx={{ transform: 'rotate(-90deg)' }}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill={'none'}
            stroke={trackColor}
            strokeWidth={stroke}
            opacity={0.5}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill={'none'}
            stroke={theme.palette.primary.main}
            strokeWidth={stroke}
            strokeLinecap={'round'}
            strokeDasharray={c}
            strokeDashoffset={c * (1 - pct)}
          />
        </Box>
        <Stack
          alignItems={'center'}
          justifyContent={'center'}
          sx={{ inset: 0, position: 'absolute' }}
        >
          <Box sx={{ alignItems: 'baseline', display: 'flex' }}>
            <Typography
              sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}
            >
              {done}
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 15,
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {`/${target}`}
            </Typography>
          </Box>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 11,
              mt: 0.3,
            }}
          >
            {'сьогодні'}
          </Typography>
        </Stack>
      </Box>
      <Box>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 13,
            fontWeight: 700,
            mb: 0.5,
          }}
        >
          {'Ціль дня'}
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>
          {remaining > 0
            ? `Ще ${remaining} ${slivWord(remaining)} до цілі`
            : 'Ціль виконано'}
        </Typography>
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: `${FLAME}1A`,
            borderRadius: 999,
            color: FLAME,
            display: 'inline-flex',
            fontSize: 13,
            fontWeight: 700,
            gap: 0.5,
            px: 1.25,
            py: 0.5,
          }}
        >
          <LocalFireDepartmentIcon sx={{ fontSize: 16 }} />
          {`${streak} днів поспіль`}
        </Box>
      </Box>
    </Stack>
  );
};

export default LearningGoalRing;
